import React from "react";

import { useHistory, useLocation } from "react-router-dom";

import { Dialog } from "primereact";
import Lottie from "react-lottie";

import BackButtom from "../../../../components/backButton";
import FormFactory from "../../../../components/form-factory";
import ResumeFiscalNote from "../../../../components/resume";
import { Fields } from "./fields";

import { DeveloperCompanyModel } from "../../../../../domain/models/developer-company.model";

import { theme } from "../../../../styles/theme";
import { CardContent, Wrapper } from "../../issue/form/styles";

import * as animationData from "../../../../assets/animations/loading.json";
import { showErrorMessage } from "../../../../helpers";
import { DocumentsModel } from "../../../../../domain/models/documents";
import { FiscalNoteModel } from "../../../../../domain/models/fiscal-note";
import { makeRemoteFiscalNote } from "../../../../../main/factories/usecases/fiscal-note/remote-fiscal-note-factory";

const isSmallScreen = window.innerWidth < 768;

type PageProps = {
  loading: boolean;
  handleSubmit: (values: any, setShowConfirm: any) => Promise<void>;
  developerCompaniesList: DeveloperCompanyModel[];
  submitting?: boolean;
};

const InvoiceForm: React.FC<PageProps> = ({
  loading,
  handleSubmit,
  developerCompaniesList,
  submitting,
}) => {
  const history = useHistory();
  const location = useLocation<{ copyNote?: DocumentsModel }>();

  const [currentNote, setCurrentNote] = React.useState<any>();
  const [showResume, setShowResume] = React.useState<boolean>(false);
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [loadingCopy, setLoadingCopy] = React.useState<boolean>(!!location.state?.copyNote);

  React.useEffect(() => {
    const copyNote = location.state?.copyNote;
    if (copyNote?.fiscalNote?.id) {
      setLoadingCopy(true);
      makeRemoteFiscalNote()
        .loadById(copyNote.fiscalNote.id)
        .then((note: FiscalNoteModel) => {
          const service = note.serviceList?.[0];
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const listNote = copyNote.fiscalNote!;
          setCurrentNote({
            company: Number(note.developerCompany?.id),
            description: note.observation,
            competencia: note.competencia ? new Date(note.competencia).toISOString().split("T")[0] : "",
            cnae: service?.cnae?.id,
            nationalService: service?.nationalService?.id,
            nbs: service?.nbs?.id,
            serviceValue: service?.serviceValue?.toString() || "",
            issAliquot: service?.issAliquot?.toString().replace(".", ",") || "0",
            iss: service?.issRetained ? "true" : "false",
            conditionedDiscount: service?.conditionedDiscount?.toString() || "0",
            unconditionalDiscount: service?.unconditionalDiscount?.toString() || "0",
            otherDeductions: service?.otherDeductions?.toString() || "0",
            taker: note.taker,
            cnaeLabel: service?.cnae ? `${service.cnae.code || service.cnae.codeFormatted} - ${service.cnae.description}` : "",
            nationalServiceLabel: service?.nationalService ? `${service.nationalService.nationalTaxCode} - ${service.nationalService.description}` : "",
            nbsLabel: service?.nbs ? `${service.nbs.codigoNbs} - ${service.nbs.description}` : "",
            serviceProvisionMode: note.serviceProvisionMode ?? listNote.serviceProvisionMode,
            serviceProvisionLink: note.serviceProvisionLink ?? listNote.serviceProvisionLink,
            goodsMovementType: note.goodsMovementType ?? listNote.goodsMovementType,
            mdicType: note.mdicType ?? listNote.mdicType,
            currencyType: note.currencyType || listNote.currencyType,
            foreignServiceValue: (note.foreignServiceValue ?? listNote.foreignServiceValue)?.toString() || "",
            providerPromotionType: note.providerPromotionType || listNote.providerPromotionType,
            clientPromotionType: note.clientPromotionType || listNote.clientPromotionType,
          });
        })
        .catch((error) => {
          console.error("Erro ao carregar nota para cópia:", error);
          showErrorMessage("Erro ao carregar dados da nota para cópia.");
        })
        .finally(() => {
          setLoadingCopy(false);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findCompanyById = (companies: any[], id: string): any =>
    companies.find((item) => item.id === id)?.company;

  const getUpdatedNoteData = (
    values: any,
    selectedCompany: any,
    cnae: any,
  ): any => ({
    ...values,
    emissionDate: new Date().toISOString(),
    simplesNacional: selectedCompany?.simpleNacional,
    subscriptionNumber: selectedCompany?.subscriptionNumber || "",
    address: selectedCompany?.address || "",
    cnae,
    hasNationalFiscal: true,
    hasForeignService: true,
  });

  const handleAddNote = async (values: any, actions: any): Promise<void> => {
    try {
      const selectedCompany = findCompanyById(
        developerCompaniesList,
        values.company,
      );

      const certificate = selectedCompany?.certificate;
      if (!certificate?.id && !certificate?.url) {
        showErrorMessage(
          "A empresa selecionada não possui um certificado digital válido. Para emitir nota fiscal, por favor, cadastre um certificado em Perfil -> Empresa.",
        );
        actions.setSubmitting(false);
        return;
      }

      const selectedCnae = values.cnae;

      const updatedNote = getUpdatedNoteData(
        values,
        selectedCompany,
        selectedCnae,
      );

      setCurrentNote((prev: any) => ({ ...prev, ...updatedNote }));
      setShowResume(true);
    } catch (error) {
      console.error("Erro ao submeter o formulário:", error);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const groups = Fields(developerCompaniesList, currentNote, setCurrentNote);

  return (
    <Wrapper>
      <BackButtom text="Emitir NFS-e para o Exterior" />
      <CardContent style={{ position: "relative" }}>
        {(loading || loadingCopy) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 9999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              height={50}
              width={150}
            />
          </div>
        )}
        {!loadingCopy && (
          <FormFactory
            groups={groups}
            enbaleCountGroup
            onSubmit={handleAddNote}
            enableReinitialize={!!location.state?.copyNote}
            backButton={{
              submit: "Voltar",
              submitting: "Voltando...",
              hide: false,
              onClick: () => history.goBack(),
              backgroundColor: theme.colors.darkBlue,
            }}
            button={{
              submit: "Gerar nota Fiscal",
              submitting: "Gerando...",
              hide: false,
              backgroundColor: theme.colors.blue400,
            }}
          />
        )}
      </CardContent>
      <Dialog
        header="Resumo da Nota Fiscal:"
        visible={showResume}
        style={{
          width: window.innerWidth < 479 ? "90vw" : "50vw",
          backgroundColor: theme.colors.darkBlue,
        }}
        headerStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
        }}
        contentStyle={{
          backgroundColor: theme.colors.darkBlue,
        }}
        footer={null}
        onHide={() => setShowResume(false)}
      >
        <ResumeFiscalNote
          submitting={submitting}
          fiscalNote={currentNote}
          onConfirmCallback={async () => {
            await handleSubmit(currentNote, setShowConfirm);
          }}
          onCancelCallback={() => setShowResume(false)}
        />
      </Dialog>
      <Dialog
        header="Aguarde!"
        visible={showConfirm}
        headerStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        style={{
          width: isSmallScreen ? "90vw" : "35vw",
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        contentStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        footer={null}
        onHide={() => null}
        closable={false}
      >
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData,
            rendererSettings: {
              preserveAspectRatio: "xMidYMid slice",
            },
          }}
          height={20}
          width={100}
          isStopped={false}
          isPaused={false}
        />
        <p className="text-center mt-4" style={{ maxWidth: "100%" }}>
          Nota fiscal sendo gerada. Você será redirecionado para tela de
          Listagem onde poderá fazer o download do documento quando estiver
          disponivel.
        </p>
      </Dialog>
    </Wrapper>
  );
};
export default InvoiceForm;
